/**
 * Placement math for the furniture info labels drawn on the quotation drawing (SRS REQ-401).
 *
 * All coordinates here are in the page's RENDERED (rotation-applied) orientation — the same space
 * as `TFurniture.coordinates` and `getPageInfo` dims, y increasing downward — so this is plain
 * axis-aligned geometry with no rotation handling (the caller runs the result through
 * `AnnotationService.applyRotation` before it reaches Apryse, exactly like every other highlight).
 *
 * Mirrors `furniturePlacement.helper.ts` (the Duplicate flow) in shape and conventions.
 */

import type { TCoordinates } from "types/elevationReferences.type"
import type { TFurnitureLabelSide } from "helpers/furnitureLabelText.helper"

/**
 * Every visual constant of a label patch, in PDF PAGE UNITS so the label scales with zoom exactly
 * like the base highlight it belongs to. Kept in one object on purpose: comparing a screenshot
 * against the backend's result file and nudging the look should be a single-line change.
 *
 * These are literal numbers / CSS color strings rather than `--clr-*` variables because the label
 * is painted by a canvas draw handler inside Apryse, which can't read CSS custom properties.
 */
export const FURNITURE_LABEL_STYLE = {
	/**
	 * Upper bound for the text height, in page units. Calibrated in the browser against a real
	 * drawing (a 2592 × 1728 sheet): at 10 a short label reads at the same size as the drawing's own
	 * callout text — big enough to scan, small enough not to dominate the elevation. A label only
	 * ever gets SMALLER than this (see `resolveLabelFontSize`).
	 */
	maxFontSize: 10,
	/**
	 * Floor for the shrink-to-fit. Below this the text stops being readable at a normal zoom, so a
	 * very long name on a very small object is allowed to overflow its highlight rather than
	 * disappear.
	 */
	minFontSize: 5,
	/** Baseline-to-baseline distance as a multiple of the (per-label) font size. */
	lineHeightRatio: 1.2,
	/** Breathing room inside the patch: left/right of the text, and above/below the text block. */
	paddingX: 3,
	paddingY: 2,
	/** Floor so a label on a tiny box is still a visible patch. */
	minWidth: 20,
	/** Distance between the patch and the base box edge (and between stacked labels). */
	gap: 2,
	/** Text is drawn black on the base annotation's own fill. */
	textColor: "#000000",
	fontFamily: "Helvetica, Arial, sans-serif",
} as const

/**
 * Font size for one label, shrunk so its text fits inside the object's own highlight.
 *
 * A single fixed size cannot work: "Countertop with a backsplash" over a narrow countertop needs a
 * much smaller face than "B24" over the same box, and any size big enough for the short label spills
 * the long one right across the drawing. So each label is scaled to its own highlight and clamped —
 * never above `maxFontSize` (short labels stay legible rather than ballooning to fill the box) and
 * never below `minFontSize` (a pathological name overflows instead of vanishing).
 *
 * @param boxWidth       width of the base highlight, in page units.
 * @param textWidthAtMax width of the widest line measured at `maxFontSize`. Canvas text width is
 *                       linear in font size, so the ratio is all we need.
 */
export const resolveLabelFontSize = (boxWidth: number, textWidthAtMax: number): number => {
	const { maxFontSize, minFontSize, paddingX } = FURNITURE_LABEL_STYLE
	if (textWidthAtMax <= 0) return maxFontSize
	const available = Math.max(0, boxWidth - 2 * paddingX)
	const fitted = maxFontSize * (available / textWidthAtMax)
	return Math.min(maxFontSize, Math.max(minFontSize, fitted))
}

/**
 * How many times a label may step further out from the box before giving up. Each side gets its own
 * full sweep of these rings, so 4 rings = 8 candidate slots.
 */
const MAX_RINGS = 4

/**
 * How far out the obstacle-avoiding passes may wander — deliberately much shorter than `MAX_RINGS`.
 *
 * A label has to read as belonging to ITS object, and past a step or two it stops doing that: it is
 * just text floating over unrelated drawing. Seen live on a two-line countertop label, which is tall
 * enough to clip the wall cabinets overhead at the near rings — an uncapped clean search sailed
 * straight past them and parked it ~90 page units away, which reads as the label having vanished.
 *
 * So staying close beats staying clear: once these rings are exhausted, the next pass comes back to
 * ring 0 and accepts an overlap. (The label-vs-label passes still use the full `MAX_RINGS` — two
 * labels on top of each other are unreadable, and that has to be escapable.)
 */
const MAX_CLEAN_RINGS = 2

/** Strict overlap — rectangles that merely share an edge are NOT considered overlapping. */
const rectsOverlap = (a: TCoordinates, b: TCoordinates): boolean =>
	a.x0 < b.x1 && a.x1 > b.x0 && a.y0 < b.y1 && a.y1 > b.y0

export type TFurnitureLabelInput = {
	/** Furniture id — also the key of the returned map, and the final tie-break for ordering. */
	id: number
	/** The base highlight's rect, in rendered page space. */
	rect: TCoordinates
	/** How many text lines the label carries (drives its height). */
	lineCount: number
	/** Side this type prefers (see `getFurnitureLabelSide`). */
	preferredSide: TFurnitureLabelSide
	/** Per-label font size from `resolveLabelFontSize` — drives both patch height and line height. */
	fontSize: number
	/**
	 * Width of the widest text line at THIS label's `fontSize`, in page units, measured by the
	 * caller with the real font. The patch is sized to wrap it, and it is what collides with a
	 * neighbour. Omit it and the patch falls back to `minWidth`.
	 */
	textWidth?: number
}

/**
 * A base furniture highlight that labels should try to stay off. Every object on the page is an
 * obstacle — including ones that carry no label of their own.
 */
export type TFurnitureLabelObstacle = {
	/** Furniture id, so a label never treats its own box as an obstacle. */
	id: number
	rect: TCoordinates
}

/**
 * Patch size for one label: a snug background behind the text, never wider than the object's own
 * highlight (which is what shrink-to-fit buys us) and never narrower than `minWidth`.
 */
export const computeLabelSize = (
	rect: TCoordinates,
	lineCount: number,
	fontSize: number,
	textWidth = 0,
): { width: number; height: number } => {
	const { minWidth, lineHeightRatio, paddingX, paddingY } = FURNITURE_LABEL_STYLE
	const boxWidth = Math.abs(rect.x1 - rect.x0)
	// Hug the text, but stay inside the highlight. `minWidth` wins on a very small box; the text may
	// then overflow, which only happens once the font has already bottomed out at `minFontSize`.
	const hugged = textWidth + 2 * paddingX
	return {
		width: Math.max(minWidth, Math.min(boxWidth, hugged)),
		height: Math.max(1, lineCount) * fontSize * lineHeightRatio + 2 * paddingY,
	}
}

/**
 * Where each label sits, for every furniture on ONE page.
 *
 * Labels are placed one at a time in a deterministic order (top-to-bottom, then left-to-right, then
 * id) so the layout is stable across re-renders. Each one takes the first slot that satisfies, in
 * strictly decreasing order of importance:
 *
 * 1. **No other label** — two labels on top of each other are unreadable, so this is never traded
 *    away (not even by the final fallback's own side choice).
 * 2. **The type's preferred side** — a countertop or wall cabinet is labelled ABOVE because
 *    everything below it belongs to the run underneath; a label that flips to the other side reads
 *    as the neighbour's. Stepping further out on the right side beats a clean slot on the wrong one.
 * 3. **Staying near its object** — a label more than `MAX_CLEAN_RINGS` out stops reading as this
 *    object's and becomes text floating over the drawing, so the search only hunts for empty space
 *    within reach.
 * 4. **Off other objects' highlights** (`obstacles`) — within reach, a label prefers empty drawing to
 *    sitting across the neighbouring cabinet. An object's OWN box is never an obstacle to itself.
 *
 * So the cascade is: preferred side clean (near rings) → preferred side accepting an overlap →
 * opposite side clean (near rings) → opposite side accepting an overlap → preferred side ring 0
 * regardless.
 *
 * What collides is the label's INK (`max(patch, text)`), which normally IS the patch — but a label
 * whose font bottomed out at `minFontSize` still overflows, and that overflow has to push its
 * neighbour aside rather than smear into it.
 *
 * Horizontally the patch is clamped inside the page; vertically a candidate that would fall off the
 * page is skipped. If every candidate fails, the label is placed on its preferred side anyway
 * (clamped into the page) — an overlapping label beats a missing one.
 */
export const computeFurnitureLabelPlacements = (
	items: TFurnitureLabelInput[],
	pageWidth: number,
	pageHeight: number,
	obstacles: TFurnitureLabelObstacle[] = [],
): Map<number, TCoordinates> => {
	const { gap } = FURNITURE_LABEL_STYLE
	const placements = new Map<number, TCoordinates>()
	const placed: TCoordinates[] = []

	const ordered = [...items].sort(
		(a, b) => a.rect.y0 - b.rect.y0 || a.rect.x0 - b.rect.x0 || a.id - b.id,
	)

	for (const item of ordered) {
		const { width, height } = computeLabelSize(item.rect, item.lineCount, item.fontSize, item.textWidth)

		// Centre the patch on the base box, then slide it fully inside the page when it would hang
		// off an edge (only shifted, never narrowed).
		const centerX = (item.rect.x0 + item.rect.x1) / 2
		let x0 = centerX - width / 2
		if (pageWidth > width) x0 = Math.min(Math.max(0, x0), pageWidth - width)
		const x1 = x0 + width

		// What actually has to stay clear of a neighbour: the wider of the patch and the text, about
		// the patch's (clamped) centre. These differ only when the font hit `minFontSize`.
		const inkWidth = Math.max(width, item.textWidth ?? 0)
		const inkCenterX = x0 + width / 2
		const inkRect = (y0: number): TCoordinates => ({
			x0: inkCenterX - inkWidth / 2,
			y0,
			x1: inkCenterX + inkWidth / 2,
			y1: y0 + height,
		})

		const step = height + gap
		const yForSide = (side: TFurnitureLabelSide, ring: number): number =>
			side === "below" ? item.rect.y1 + gap + ring * step : item.rect.y0 - gap - ring * step - height

		const oppositeSide: TFurnitureLabelSide = item.preferredSide === "below" ? "above" : "below"

		// Boxes this label must try to stay off: every other object's highlight on the page.
		const blockers = obstacles.filter((obstacle) => obstacle.id !== item.id).map(({ rect }) => rect)

		/** First slot free of already-placed labels on ONE side, stepping out ring by ring. */
		const findSlot = (side: TFurnitureLabelSide, avoidBlockers: boolean): number | null => {
			const rings = avoidBlockers ? MAX_CLEAN_RINGS : MAX_RINGS
			for (let ring = 0; ring < rings; ring++) {
				const y0 = yForSide(side, ring)
				if (y0 < 0 || y0 + height > pageHeight) continue
				const ink = inkRect(y0)
				if (placed.some((rect) => rectsOverlap(ink, rect))) continue
				if (avoidBlockers && blockers.some((rect) => rectsOverlap(ink, rect))) continue
				return y0
			}
			return null
		}

		// Side beats cleanliness: a countertop or wall cabinet is labelled ABOVE because everything
		// below it belongs to the run underneath, so a label that flips down reads as the
		// neighbour's. Only once the preferred side is unusable at every ring — even accepting an
		// overlap — does the opposite side come into play. Note `avoidBlockers: false` still keeps
		// labels off each OTHER; that separation is never traded away.
		const chosenY =
			findSlot(item.preferredSide, true) ??
			findSlot(item.preferredSide, false) ??
			findSlot(oppositeSide, true) ??
			findSlot(oppositeSide, false) ??
			// Every ring on both sides was taken or off-page — fall back to the preferred side,
			// clamped into the page, and accept the overlap rather than dropping the label entirely.
			Math.min(Math.max(0, yForSide(item.preferredSide, 0)), Math.max(0, pageHeight - height))

		// The PATCH is what gets drawn; the wider ink rect is what later labels must avoid.
		placements.set(item.id, { x0, y0: chosenY, x1, y1: chosenY + height })
		placed.push(inkRect(chosenY))
	}

	return placements
}
